import React, { useRef } from 'react';

import styled, { css } from 'styled-components';

import { Component as Bounceback } from '../Bounceback';
import { CartUpsellCarousel } from '../CartUpsellCarousel';
import { Component as NewPricingSkinnyBanner } from '../NewPricingSkinnyBanner';
import QuickViewModal from '../QuickViewModal/QuickViewModal';
import SpinningLoader from '../SpinningLoader/SpinningLoader';
import { Component as StyleContext } from '../StyleContext';
import ToastMessages from '../ToastMessages/ToastMessages';

import { CartDrawerBasketItemList } from './CartDrawerBasketItemList';
import { CartDrawerHeader } from './CartDrawerHeader';
import { CartDrawerInfluencerToastMessage } from './CartDrawerInfluencerToastMessage';
import { CartDrawerBody, Content, Drawer } from './CartDrawerWrapper';
import { NewCartDrawerFreeShippingProgressBar } from './NewCartDrawerFreeShippingProgressBar';
import { NewCartDrawerMemberCreditsToggle } from './NewCartDrawerMemberCreditsToggle';
import { NewCartDrawerPromoProgressBar } from './NewCartDrawerPromoProgressBar';
import { useShowShoppingBagLoadingIndicator } from './useShowShoppingBagLoadingIndicator';
import { useVisibility } from './useVisibility';


const NewCartDrawerWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.small}px;
`;

const contentWrapperStyling = css`
  width: 474px;
  display: block;
`;

const headerWrapperStyling = css`
  padding: 24px 16px;
  border: none;
`;

const headingStyling = css`
  margin-right: auto;
`;

const closeButtonStyling = css`
  color: ${({ theme }) => theme.colors.black};
  pointer-events: all;
`;

const SpinningLoaderWrapper = styled.div`
  ${SpinningLoader.SpinnerWrapper} {
    top: 64px;
    height: calc(100% - 64px);
  }
`;

const CollapsableFooterWrapper = styled.div<{
  hidden: boolean;
  showLoadingIndicator: boolean;
}>`
  display: flex;
  justify-content: space-around;
  background: white;
  position: sticky;
  bottom: 0;
  padding: 18px 16px;
  gap: 12px;
  transition: box-shadow 0.1s ease-out;
  box-shadow: 0 -2px 8px 0 #18181e1a;
  transition: all 0.1s ease-in-out;
  z-index: ${({ showLoadingIndicator, theme }) =>
    showLoadingIndicator ? theme.zIndex.navItems : theme.zIndex.floatingBar};
  ${({ hidden }) =>
    hidden &&
    `
    height: 0;
    padding-top: 0;
    padding-bottom: 0;
    overflow: hidden;
  `}
`;

const StyledCartDrawerBody = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const PROMO_BAR_UPDATED = false;

export function NewCartDrawerDesktopTabletRenderer({
  collapsableFooter,
  expandedFooter,
}) {
  const { showLoadingIndicator } = useShowShoppingBagLoadingIndicator();

  const bodyRef = useRef<HTMLDivElement>(null);
  const { divRef: expandedFooterRef, isVisible: isExpandedFooterVisible } =
    useVisibility({
      threshold: 0.8,
      root: bodyRef,
    });

  return (
    <Drawer>
      <SpinningLoaderWrapper>
        {showLoadingIndicator && (
          <SpinningLoader
            isLoading
            height={24}
            width={24}
            shouldFadeBackground
            color="#1c1b1f"
          />
        )}
      </SpinningLoaderWrapper>
      <StyleContext context="cartDrawer">
        <ToastMessages data-autotag="mini-cart-toast-message" />
        <Content contentWrapperStyling={contentWrapperStyling}>
          <NewPricingSkinnyBanner />
          <CartDrawerHeader
            headerWrapperStyling={headerWrapperStyling}
            closeButtonStyling={closeButtonStyling}
            headingStyling={headingStyling}
          />
          <Bounceback shouldShowTerms={false} />
          <NewCartDrawerWrapper>
            <NewCartDrawerFreeShippingProgressBar />
          </NewCartDrawerWrapper>
          {PROMO_BAR_UPDATED && <NewCartDrawerPromoProgressBar />}
          <StyledCartDrawerBody ref={bodyRef}>
            <NewCartDrawerWrapper>
              <NewCartDrawerMemberCreditsToggle />
            </NewCartDrawerWrapper>
            <CartDrawerBody>
              <CartDrawerInfluencerToastMessage />
              <CartDrawerBasketItemList />
              <CartUpsellCarousel />
              <div ref={expandedFooterRef}>{expandedFooter}</div>
            </CartDrawerBody>
            {collapsableFooter && expandedFooterRef.current && (
              <CollapsableFooterWrapper
                hidden={isExpandedFooterVisible}
                showLoadingIndicator={showLoadingIndicator}
              >
                {collapsableFooter}
              </CollapsableFooterWrapper>
            )}
          </StyledCartDrawerBody>
        </Content>
        <QuickViewModal />
      </StyleContext>
    </Drawer>
  );
}
